import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Plus, PencilSimple, Trash, ArrowRight, Eye, EyeSlash, ChartLine } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  // Create dialog state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "General",
    level: "Beginner",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/courses");
      setCourses(data);
    } catch (e) {
      setError(formatApiError(e, "Could not load courses."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreating(true);
    try {
      const { data } = await api.post("/courses", { ...form, published: false, modules: [] });
      setForm({ title: "", description: "", category: "General", level: "Beginner" });
      setOpen(false);
      // Jump straight to the editor so the admin can add modules + upload videos
      navigate(`/admin/courses/${data.id}`);
    } catch (err) {
      setCreateError(formatApiError(err, "Could not create course."));
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/courses/${deleteId}`);
      setDeleteId(null);
      await reload();
    } catch (e) {
      setError(formatApiError(e, "Could not delete course."));
    }
  };

  const togglePublish = async (course) => {
    // Guard against publishing a totally empty course
    const totalLessons = (course.modules || []).reduce(
      (s, m) => s + (m.lessons?.length || 0),
      0
    );
    if (!course.published && totalLessons === 0) {
      setError(
        "Add at least one module and one lesson before publishing this course."
      );
      return;
    }
    try {
      await api.put(`/courses/${course.id}`, { published: !course.published });
      await reload();
    } catch (e) {
      setError(formatApiError(e, "Could not change publish state."));
    }
  };

  return (
    <AppLayout
      title="Manage Courses"
      eyebrow="Administration"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg"
              data-testid="admin-new-course-button"
            >
              <Plus size={16} weight="bold" className="mr-2" /> New course
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
                Create a new course
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4 mt-4">
              <div>
                <Label className="eyebrow">Title</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-2 rounded-lg"
                  data-testid="course-form-title"
                />
              </div>
              <div>
                <Label className="eyebrow">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-2 rounded-lg"
                  rows={3}
                  data-testid="course-form-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="eyebrow">Category</Label>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-2 rounded-lg"
                    data-testid="course-form-category"
                  />
                </div>
                <div>
                  <Label className="eyebrow">Level</Label>
                  <Input
                    value={form.level}
                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                    className="mt-2 rounded-lg"
                    data-testid="course-form-level"
                  />
                </div>
              </div>
              {createError && <div className="text-sm text-[#993333]">{createError}</div>}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={creating}
                  className="bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg"
                  data-testid="course-form-submit"
                >
                  {creating ? "Creating..." : "Create course"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      <div className="border border-[#EAEAE6] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#EAEAE6] bg-[#F4F4F0] text-xs uppercase tracking-[0.15em] font-bold text-[#8A8A85]">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-1">Modules</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#8A8A85]">Loading...</div>
        ) : courses.length === 0 ? (
          <div className="p-10 text-center">
            <div className="eyebrow mb-3">Empty</div>
            <h3 className="font-heading text-2xl font-bold mb-2">No courses yet</h3>
            <p className="text-[#5C5C58] mb-4">Create your first course to start training employees.</p>
          </div>
        ) : (
          courses.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#EAEAE6] last:border-b-0 items-center hover:bg-[#F4F4F0]"
              data-testid={`admin-course-row-${c.id}`}
            >
              <Link
                to={`/admin/courses/${c.id}`}
                className="col-span-5 group"
                data-testid={`admin-course-title-${c.id}`}
              >
                <div className="font-semibold group-hover:text-[#E25822] transition-colors">{c.title}</div>
                <div className="text-xs text-[#8A8A85] line-clamp-1">{c.description || "No description yet"}</div>
              </Link>
              <div className="col-span-2 text-sm text-[#3D3D38]">{c.category || "General"}</div>
              <div className="col-span-1 font-mono text-sm">{c.modules?.length || 0}</div>
              <div className="col-span-2">
                <button
                  onClick={() => togglePublish(c)}
                  data-testid={`admin-toggle-publish-${c.id}`}
                  title={c.published ? "Click to unpublish" : "Click to publish"}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${
                    c.published
                      ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                      : "bg-[#EAEAE6] text-[#3D3D38] border-[#EAEAE6] hover:bg-[#EAEAE6]"
                  }`}
                >
                  {c.published ? (
                    <>
                      <Eye size={12} weight="fill" /> Published
                    </>
                  ) : (
                    <>
                      <EyeSlash size={12} /> Draft
                    </>
                  )}
                </button>
              </div>
              <div className="col-span-2 flex items-center gap-2 justify-end">
                <Link
                  to={`/admin/analytics/course/${c.id}`}
                  className="p-2 border border-[#EAEAE6] hover:border-[#E25822] hover:text-[#E25822] transition-colors"
                  data-testid={`admin-course-analytics-${c.id}`}
                  title="View analytics"
                >
                  <ChartLine size={16} />
                </Link>
                <Link
                  to={`/admin/courses/${c.id}`}
                  className="px-3 py-2 bg-[#E25822] hover:bg-[#C44E1A] text-white text-xs font-medium inline-flex items-center gap-1.5"
                  data-testid={`admin-edit-${c.id}`}
                >
                  <PencilSimple size={14} /> Edit & Upload
                </Link>
                <button
                  onClick={() => setDeleteId(c.id)}
                  className="p-2 border border-[#EAEAE6] hover:border-[#993333] hover:text-[#993333]"
                  data-testid={`admin-delete-${c.id}`}
                  title="Delete"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the course, all its modules and lessons, plus all enrollments and progress.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-[#993333] hover:bg-[#7E2828] text-white rounded-lg"
              data-testid="confirm-delete-course"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
